import { Shield, Lock, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";

interface TrustBadgesProps {
  variant?: "full" | "payment" | "security" | "minimal";
  className?: string;
  showLovable?: boolean;
  showStripe?: boolean;
  showSSL?: boolean;
  showCards?: boolean;
}

export const TrustBadges = ({
  variant = "full",
  className,
  showLovable = true,
  showStripe = true,
  showSSL = true,
  showCards = true,
}: TrustBadgesProps) => {
  const showAll = variant === "full";
  const showPaymentOnly = variant === "payment";
  const showSecurityOnly = variant === "security";
  const showMinimalOnly = variant === "minimal";

  return (
    <div className={cn("flex flex-wrap items-center justify-center gap-4", className)}>
      {/* Powered by Lovable */}
      {showLovable && (showAll || showMinimalOnly) && (
        <a
          href="https://lovable.dev"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-1.5 bg-background/80 border border-border rounded-full text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-primary"
          >
            <path
              d="M50 10C27.909 10 10 27.909 10 50s17.909 40 40 40 40-17.909 40-40S72.091 10 50 10zm0 70c-16.569 0-30-13.431-30-30s13.431-30 30-30 30 13.431 30 30-13.431 30-30 30z"
              fill="currentColor"
            />
            <path
              d="M50 25c-13.807 0-25 11.193-25 25s11.193 25 25 25 25-11.193 25-25-11.193-25-25-25zm0 40c-8.284 0-15-6.716-15-15s6.716-15 15-15 15 6.716 15 15-6.716 15-15 15z"
              fill="currentColor"
            />
            <circle cx="50" cy="50" r="8" fill="currentColor" />
          </svg>
          <span>Powered by <strong className="text-foreground">Lovable</strong></span>
        </a>
      )}

      {/* Secure Payments by Stripe */}
      {showStripe && (showAll || showPaymentOnly) && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-background/80 border border-border rounded-full text-xs text-muted-foreground">
          <svg
            width="16"
            height="16"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.908 11.21c0-1.123.92-1.556 2.444-1.556 2.186 0 4.947.663 7.133 1.845V5.277c-2.389-.948-4.75-1.321-7.133-1.321-5.833 0-9.717 3.049-9.717 8.137 0 7.932 10.922 6.668 10.922 10.09 0 1.33-1.156 1.76-2.771 1.76-2.4 0-5.467-.99-7.897-2.316v6.28c2.69 1.156 5.406 1.649 7.897 1.649 5.977 0 10.085-2.956 10.085-8.123-.026-8.56-10.963-7.038-10.963-10.223z"
              fill="#635BFF"
            />
          </svg>
          <span>Secure payments by <strong className="text-[#635BFF]">Stripe</strong></span>
        </div>
      )}

      {/* Credit Card Icons */}
      {showCards && (showAll || showPaymentOnly) && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background/80 border border-border rounded-full">
          {/* Apple Pay */}
          <div className="flex items-center justify-center bg-black rounded px-2 py-0.5 gap-0.5" style={{ height: 18 }}>
            <svg width="11" height="13" viewBox="0 0 256 315" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M213.803 167.03c.442 47.58 41.74 63.413 42.197 63.615-.35 1.116-6.599 22.563-21.757 44.716-13.104 19.153-26.705 38.235-48.13 38.63-21.05.388-27.82-12.483-51.888-12.483-24.061 0-31.582 12.088-51.51 12.871-20.68.783-36.428-20.71-49.64-39.793C7.228 235.392-18.307 162.08 7.937 112.982c13.043-24.43 36.348-39.916 61.636-40.31 20.304-.39 39.467 13.652 51.873 13.652 12.404 0 35.687-16.895 60.174-14.414 10.244.427 39.026 4.14 57.503 31.186-1.49.923-34.335 20.044-33.978 59.822l.658 4.112zM174.24 50.199c10.98-13.29 18.369-31.79 16.353-50.199-15.826.636-34.962 10.546-46.314 23.828-10.173 11.763-19.082 30.589-16.678 48.633 17.64 1.365 35.66-8.964 46.639-22.262"/>
            </svg>
            <span className="text-white font-semibold" style={{ fontSize: 9, lineHeight: 1, letterSpacing: '-0.02em' }}>Pay</span>
          </div>
          {/* Visa */}
          <svg width="28" height="18" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="48" height="32" rx="4" fill="#1A1F71"/>
            <path d="M19.5 21H17L18.75 11H21.25L19.5 21ZM15.5 11L13.1 18.1L12.8 16.6L12.8 16.6L11.9 12.1C11.9 12.1 11.8 11 10.4 11H6.1L6 11.3C6 11.3 7.6 11.6 9.4 12.7L11.6 21H14.2L18.1 11H15.5ZM35.5 21H37.8L35.8 11H33.8C32.6 11 32.3 11.9 32.3 11.9L28.6 21H31.2L31.7 19.5H34.9L35.5 21ZM32.5 17.4L33.9 13.4L34.7 17.4H32.5ZM28.5 13.7L28.9 11.3C28.9 11.3 27.5 10.8 26 10.8C24.4 10.8 20.8 11.5 20.8 14.6C20.8 17.5 24.8 17.5 24.8 19C24.8 20.5 21.2 20.1 19.9 19.1L19.5 21.6C19.5 21.6 20.9 22.2 23 22.2C25.1 22.2 28.4 21.1 28.4 18.3C28.4 15.4 24.4 15.1 24.4 13.9C24.4 12.7 27.2 12.9 28.5 13.7Z" fill="white"/>
          </svg>
          {/* Mastercard */}
          <svg width="28" height="18" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="48" height="32" rx="4" fill="#000"/>
            <circle cx="19" cy="16" r="8" fill="#EB001B"/>
            <circle cx="29" cy="16" r="8" fill="#F79E1B"/>
            <path d="M24 10.5C25.9 12 27.2 14.3 27.2 16.9C27.2 19.5 25.9 21.8 24 23.3C22.1 21.8 20.8 19.5 20.8 16.9C20.8 14.3 22.1 12 24 10.5Z" fill="#FF5F00"/>
          </svg>
          {/* Amex */}
          <svg width="28" height="18" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="48" height="32" rx="4" fill="#006FCF"/>
            <text x="24" y="20" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif">AMEX</text>
          </svg>
        </div>
      )}

      {/* SSL/Encryption Badge */}
      {showSSL && (showAll || showSecurityOnly) && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full text-xs text-green-700 dark:text-green-400">
          <Lock className="h-3.5 w-3.5" />
          <span>256-bit SSL Encrypted</span>
        </div>
      )}

      {/* Security Shield */}
      {(showAll || showSecurityOnly) && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-full text-xs text-blue-700 dark:text-blue-400">
          <Shield className="h-3.5 w-3.5" />
          <span>Secure Platform</span>
        </div>
      )}
    </div>
  );
};

// Compact inline version for buttons
export const TrustBadgeInline = ({ type }: { type: "ssl" | "stripe" | "secure" }) => {
  if (type === "ssl") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
        <Lock className="h-3 w-3" />
        Secure
      </span>
    );
  }

  if (type === "stripe") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <svg width="12" height="12" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.908 11.21c0-1.123.92-1.556 2.444-1.556 2.186 0 4.947.663 7.133 1.845V5.277c-2.389-.948-4.75-1.321-7.133-1.321-5.833 0-9.717 3.049-9.717 8.137 0 7.932 10.922 6.668 10.922 10.09 0 1.33-1.156 1.76-2.771 1.76-2.4 0-5.467-.99-7.897-2.316v6.28c2.69 1.156 5.406 1.649 7.897 1.649 5.977 0 10.085-2.956 10.085-8.123-.026-8.56-10.963-7.038-10.963-10.223z"
            fill="#635BFF"
          />
        </svg>
        Stripe
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
      <Shield className="h-3 w-3" />
      Protected
    </span>
  );
};
